import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, MapPin, Calendar, Droplets, Users, AlertTriangle, BarChart3 } from "lucide-react";
import { PortalShell } from "@/components/PortalShell";
import { Input } from "@/components/ui/input";
import { ShowTestDataToggle, useShowDemoToggle } from "@/components/ShowTestDataToggle";
import { ArchiveStatusFilter, useArchiveFilter } from "@/components/ArchiveStatusFilter";
import { ArchiveActionsMenu } from "@/components/ArchiveActionsMenu";
import { StatusBadge } from "@/components/StatusBadge";
import api from "@/lib/api";
import { formatDateAU } from "@/lib/formatDateAU";
import { BAND_CFG } from "@/components/passport/HealthScoreGauge";

const BAND_ORDER = ["excellent", "good", "fair", "attention", "action"];

export default function AdminProperties() {
  const [stats, setStats] = useState(null);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [showDemo, setShowDemo] = useShowDemoToggle("admin.properties.showDemo");
  const [archived, setArchived] = useArchiveFilter("admin.properties.archived");
  const [me, setMe] = useState(null);

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => setMe(data)).catch(() => setMe(null));
  }, []);

  const load = async (q = "") => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (showDemo) params.set("show_demo", "true");
      if (archived !== "active") params.set("archived", archived);
      const qs = params.toString() ? `?${params.toString()}` : "";
      const [statsRes, propsRes] = await Promise.all([
        api.get("/admin/passport/stats"),
        api.get("/admin/properties" + qs),
      ]);
      setStats(statsRes.data);
      setProperties(propsRes.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(query); }, [showDemo, archived]);
  useEffect(() => {
    const t = setTimeout(() => load(query), 400);
    return () => clearTimeout(t);
  }, [query]);

  const fmt = (d) => formatDateAU(d);
  const now = new Date();

  return (
    <PortalShell title="Properties" subtitle="AWARE Water Health Record — Admin">
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-7">
        <div data-testid="admin-properties-stat-total" className="rounded-2xl bg-white border border-slate-200 p-5">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-bold text-slate-500">
            <Users className="w-3.5 h-3.5" /> Active properties
          </div>
          <div className="font-display text-3xl font-bold text-slate-900 mt-2">{stats?.total_properties ?? "—"}</div>
        </div>
        <div data-testid="admin-properties-stat-tests" className="rounded-2xl bg-white border border-slate-200 p-5">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-bold text-slate-500">
            <Droplets className="w-3.5 h-3.5" /> Tests on file
          </div>
          <div className="font-display text-3xl font-bold text-slate-900 mt-2">{stats?.total_tests ?? "—"}</div>
        </div>
        <div data-testid="admin-properties-stat-due" className="rounded-2xl bg-amber-50 border border-amber-200 p-5">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-bold text-amber-700">
            <AlertTriangle className="w-3.5 h-3.5" /> Due / overdue
          </div>
          <div className="font-display text-3xl font-bold text-amber-900 mt-2">{stats?.due_for_retest ?? "—"}</div>
        </div>
        <div data-testid="admin-properties-stat-avg" className="rounded-2xl bg-slate-900 text-white p-5">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-bold text-cyan-400">
            <BarChart3 className="w-3.5 h-3.5" /> Avg Health Score™
          </div>
          <div className="font-display text-3xl font-bold mt-2">{stats?.avg_health_score ?? "—"}</div>
        </div>
      </div>

      {/* Band distribution */}
      {stats?.band_distribution && Object.keys(stats.band_distribution).length > 0 && (
        <div className="rounded-2xl bg-white border border-slate-200 p-5 mb-7">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-3">Score band distribution</div>
          <div className="flex h-3 rounded-full overflow-hidden">
            {BAND_ORDER.map((b) => {
              const cnt = stats.band_distribution[b] || 0;
              if (cnt === 0) return null;
              const pct = (cnt / stats.total_tests) * 100;
              return <div key={b} style={{ width: `${pct}%`, background: BAND_CFG[b].color }} title={`${BAND_CFG[b].label}: ${cnt}`} />;
            })}
          </div>
          <div className="flex flex-wrap gap-4 mt-3">
            {BAND_ORDER.map((b) => {
              const cnt = stats.band_distribution[b] || 0;
              return (
                <div key={b} className="flex items-center gap-1.5 text-xs">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ background: BAND_CFG[b].color }} />
                  <span className="text-slate-600">{BAND_CFG[b].label}</span>
                  <span className="font-bold text-slate-900">{cnt}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Search */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            data-testid="admin-properties-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search address, owner email or nickname"
            className="pl-10 h-11 rounded-full bg-white"
          />
        </div>
        <ShowTestDataToggle showDemo={showDemo} onChange={setShowDemo} testId="properties-show-demo-toggle" />
        <ArchiveStatusFilter value={archived} onChange={setArchived} testId="properties-archive-filter" />
      </div>

      {/* Properties table */}
      <div data-testid="admin-properties-table" className="rounded-3xl bg-white border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-500">Loading properties...</div>
        ) : properties.length === 0 ? (
          <div className="p-10 text-center text-slate-500">No properties match your search.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Property</th>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Owner</th>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Sources</th>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Tests</th>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Latest score</th>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Next retest</th>
                  <th className="text-right p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {properties.map(({ property: p, latest_test, next_retest, test_count }) => {
                  const band = latest_test?.band;
                  const cfg = band ? BAND_CFG[band] : null;
                  const dueDate = next_retest ? new Date(next_retest.next_due_at) : null;
                  const overdue = dueDate && dueDate < now;
                  return (
                    <tr key={p.id} data-testid={`admin-property-row-${p.id}`} className="border-t border-slate-100 hover:bg-slate-50/50">
                      <td className="p-4">
                        <Link
                          to={`/admin/properties/${p.id}`}
                          data-testid={`admin-property-link-${p.id}`}
                          className="font-semibold text-slate-900 hover:text-cyan-700 hover:underline underline-offset-2 cursor-pointer transition-colors block"
                        >
                          {p.nickname || p.address}
                        </Link>
                        {p.nickname && (
                          <Link
                            to={`/admin/properties/${p.id}`}
                            className="text-xs text-slate-500 hover:text-cyan-700 hover:underline underline-offset-2 block"
                          >
                            {p.address}
                          </Link>
                        )}
                        <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-0.5 inline-flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> {p.property_type}
                        </div>
                      </td>
                      <td className="p-4 text-slate-700 text-xs">{p.owner_email || "—"}</td>
                      <td className="p-4">
                        <div className="flex flex-wrap gap-1">
                          {(p.sources || []).map((s) => (
                            <span key={s.id} className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-700 capitalize">
                              {s.type.replace("_", " ")}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="p-4 font-bold text-slate-900">{test_count}</td>
                      <td className="p-4">
                        {latest_test && cfg ? (
                          <div className="inline-flex items-center gap-2">
                            <span className="font-display font-bold text-lg text-slate-900">{latest_test.health_score}</span>
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold text-white" style={{ background: cfg.color }}>
                              {cfg.label}
                            </span>
                          </div>
                        ) : (
                          <span className="text-slate-400 text-xs">—</span>
                        )}
                      </td>
                      <td className="p-4">
                        {next_retest ? (
                          <span className={`inline-flex items-center gap-1 text-xs font-semibold ${overdue ? "text-rose-600" : "text-slate-700"}`}>
                            <Calendar className="w-3 h-3" /> {fmt(next_retest.next_due_at)}
                            {overdue && <span className="ml-1 px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 text-[9px] font-bold uppercase">Overdue</span>}
                          </span>
                        ) : (
                          <span className="text-slate-400 text-xs">—</span>
                        )}
                      </td>
                      <td className="p-4 text-right">
                        <div className="inline-flex items-center gap-2">
                          {p.archived_at && <StatusBadge status="archived" />}
                          <ArchiveActionsMenu
                            recordType="property"
                            recordId={p.id}
                            recordDisplay={p.nickname || p.address}
                            isArchived={!!p.archived_at}
                            onChanged={() => load(query)}
                            hidden={me?.role && me.role !== "admin"}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </PortalShell>
  );
}
